import { useState, useEffect, useRef, useMemo, useCallback, type ReactNode } from 'react'
import { NavLink, useLocation, useParams } from 'react-router-dom'
import { Plus, Search, MapPin, PanelRightClose, ChevronRight, ChevronDown, Users } from '@/ui/icons'
import { Button, HoverCard, HoverCardContent, HoverCardTrigger, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/components'
import { Input } from '@/ui/components'
import { useTrips } from '@/modules/trips'
import { useTripHasFetched } from '@/stores'
import { useAuth } from '@/modules/auth'
import { useCreateModal } from '@/contexts/CreateModalContext'
import { TripsSidebarSkeleton } from '@/ui/components'
import { formatTripDateRange } from '@/modules/trips/utils/dateHelpers'
import type { TripResponse } from '@/shared/api/types'
import { cn } from '@/shared/utils'
import { createApiClient } from '@/shared/api/client'
import { getTripCoverUrl } from '@/shared/constants'
import { SidebarRecentActivity } from '@/modules/dashboard/components'
import {
  TRIPS_SIDEBAR_EXPANDED_WIDTH_PX as SIDEBAR_WIDTH,
  TRIPS_SIDEBAR_COLLAPSED_WIDTH_PX as COLLAPSED_WIDTH,
} from './tripsSidebarDimensions'

export { TRIPS_SIDEBAR_EXPANDED_WIDTH_PX, TRIPS_SIDEBAR_COLLAPSED_WIDTH_PX } from './tripsSidebarDimensions'

/** Sort option for trips list: upcoming (by start_date) or last updated (by updated_at), each asc/desc */
export type TripSortOption = 'upcoming_desc' | 'upcoming_asc' | 'updated_desc' | 'updated_asc'

const TRIP_SORT_OPTIONS: { value: TripSortOption; label: string }[] = [
  { value: 'upcoming_desc', label: 'Upcoming (newest first)' },
  { value: 'upcoming_asc', label: 'Upcoming (oldest first)' },
  { value: 'updated_desc', label: 'Last updated (newest first)' },
  { value: 'updated_asc', label: 'Last updated (oldest first)' }
]

function getSortParams(sort: TripSortOption): { sort_by: 'start_date' | 'updated_at'; sort_order: 'asc' | 'desc' } {
  if (sort.startsWith('upcoming_')) {
    return { sort_by: 'start_date', sort_order: sort === 'upcoming_desc' ? 'desc' : 'asc' }
  }
  return { sort_by: 'updated_at', sort_order: sort === 'updated_desc' ? 'desc' : 'asc' }
}

type TripGroup = 'active' | 'planning' | 'completed'

function getTripGroup(trip: TripResponse): TripGroup {
  const now = new Date()
  const startDate = trip.start_date ? new Date(trip.start_date) : null
  const endDate = trip.end_date ? new Date(trip.end_date) : null
  const completedAt = trip.completed_at ? new Date(trip.completed_at) : null

  if (startDate && endDate && now >= startDate && now <= endDate) return 'active'
  if (completedAt || (endDate && now > endDate)) return 'completed'
  return 'planning'
}

function getGroupLabel(group: TripGroup) {
  switch (group) {
    case 'active':
      return 'Active'
    case 'planning':
      return 'Planning'
    case 'completed':
      return 'Completed / Archived'
  }
}

function getTripLocation(trip: TripResponse): string {
  if (trip.meetup_location) return trip.meetup_location
  return 'No location set'
}

interface TripSidebarItemProps {
  trip: TripResponse
  coverUrl: string | undefined
  isActive: boolean
}

function TripSidebarItem({ trip, coverUrl, isActive }: TripSidebarItemProps) {
  const crewCount = trip.member_count ?? 0
  const dateRange = formatTripDateRange(trip.start_date, trip.end_date)

  return (
    <HoverCard openDelay={400} closeDelay={150}>
      <HoverCardTrigger asChild>
        <NavLink
          to={`/trips/${trip.id}`}
          className={cn(
            'flex flex-col rounded-md overflow-hidden transition-colors block',
            isActive ? 'bg-adventure-100 text-adventure-900' : 'hover:bg-earth-100 text-earth-800'
          )}
        >
          {/* Cover photo - taller strip for better thumbnail presence */}
          <div className="relative w-full aspect-[5/2] min-h-[72px] rounded-t-md overflow-hidden bg-earth-100">
            <img
              src={coverUrl}
              alt={trip.name}
              className="w-full h-full object-cover"
            />
            {/* Title overlay on bottom-left */}
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent pt-4 px-2 pb-0.5">
              <span className="font-medium text-white truncate block text-xs leading-tight">{trip.name}</span>
            </div>
          </div>
          {/* Meta row: date range prominent, then crew */}
          <div className="px-2 py-2 flex flex-col gap-0.5 min-w-0">
            {dateRange && (
              <span className="text-xs font-medium text-earth-800 dark:text-foreground truncate">{dateRange}</span>
            )}
            <span className="text-xs text-earth-500 dark:text-earth-300 truncate">
              {crewCount ? `${crewCount} crew` : 'No crew yet'}
            </span>
          </div>
        </NavLink>
      </HoverCardTrigger>
      <HoverCardContent
        side="right"
        align="start"
        sideOffset={8}
        className="w-80 p-0 overflow-hidden border-earth-200 bg-earth-50"
      >
        <div className="flex flex-col">
          <div className="relative w-full h-44 overflow-hidden bg-earth-100">
            <img
              src={coverUrl}
              alt={trip.name}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="p-4 space-y-2">
            <h3 className="font-semibold text-earth-900 dark:text-foreground">{trip.name}</h3>
            {trip.description && (
              <p className="text-sm text-earth-600 dark:text-earth-300 line-clamp-3">{trip.description}</p>
            )}
            <div className="flex flex-col gap-1.5 pt-1 text-sm text-earth-600 dark:text-earth-400">
              {dateRange && (
                <span>{dateRange}</span>
              )}
              <span className="flex items-center gap-2">
                <MapPin className="h-3.5 w-3.5 flex-shrink-0 text-adventure-500" aria-hidden />
                {getTripLocation(trip)}
              </span>
              <span className="flex items-center gap-2">
                <Users className="h-3.5 w-3.5 flex-shrink-0 text-earth-500" aria-hidden />
                {crewCount} crew
              </span>
            </div>
          </div>
        </div>
      </HoverCardContent>
    </HoverCard>
  )
}

/** Reusable trip list content (search + grouped items). Used in desktop sidebar and mobile Sheet. */
export interface TripsSidebarContentProps {
  /** Right side of the header (e.g. Create trip + Collapse for sidebar, or Create trip + Close for sheet) */
  headerRight?: ReactNode
}

export function TripsSidebarContent({ headerRight }: TripsSidebarContentProps) {
  const location = useLocation()
  const { tripId } = useParams()
  const { getAccessToken } = useAuth()
  const { trips, isLoading, error, loadTrips } = useTrips()
  const { openCreateModal } = useCreateModal()
  const [searchTerm, setSearchTerm] = useState('')
  const [sortOption, setSortOption] = useState<TripSortOption>('upcoming_desc')
  const [coverUrls, setCoverUrls] = useState<Record<string, string>>({})
  const [sectionExpanded, setSectionExpanded] = useState<Record<TripGroup, boolean>>({
    active: true,
    planning: true,
    completed: false
  })

  const fetchTrips = useCallback(() => {
    const { sort_by, sort_order } = getSortParams(sortOption)
    loadTrips({ includeArchived: true, sort_by, sort_order }, true)
  }, [sortOption, loadTrips])

  // This component is the authoritative loader for trips.
  // Always fetches with includeArchived so archived trips appear in the sidebar.
  // On mount: force fetch to get fresh data.
  // On subsequent navigations back to /trips: force fetch again.
  const prevPathnameRef = useRef<string>(location.pathname)
  const hasFetchedForPath = useRef<string | null>(null)

  const prevSortRef = useRef<TripSortOption | undefined>(undefined)

  useEffect(() => {
    const isTripsPage = location.pathname.startsWith('/trips')
    const isFirstMount = hasFetchedForPath.current === null
    const isNavigation = prevPathnameRef.current !== location.pathname

    if (isTripsPage && (isFirstMount || isNavigation)) {
      fetchTrips()
      hasFetchedForPath.current = location.pathname
    }
    prevPathnameRef.current = location.pathname
  }, [location.pathname, fetchTrips])

  // Refetch when user changes sort (skip initial mount; first load is handled above)
  useEffect(() => {
    if (!location.pathname.startsWith('/trips')) return
    const prevSort = prevSortRef.current
    prevSortRef.current = sortOption
    if (prevSort !== undefined && prevSort !== sortOption) {
      fetchTrips()
    }
  }, [sortOption, location.pathname, fetchTrips])

  useEffect(() => {
    const apiClient = createApiClient(getAccessToken)
    const fetchMissing = async () => {
      const pending: Array<Promise<void>> = []
      for (const trip of trips ?? []) {
        const id = trip.id
        const coverPhotoId = trip.cover_photo_id
        if (!trip.cover_photo_url && coverPhotoId && !coverUrls[id]) {
          pending.push(
            apiClient
              .get<{ url: string }>(`/trips/photos/${coverPhotoId}/url`)
              .then((resp) => {
                setCoverUrls((prev) => ({ ...prev, [id]: resp.url }))
              })
              .catch(() => {})
          )
        }
      }
      if (pending.length > 0) {
        await Promise.all(pending)
      }
    }
    if (trips && trips.length > 0) {
      fetchMissing()
    }
  }, [trips, getAccessToken])

  const groupedTrips = useMemo(() => {
    const filtered = (trips ?? []).filter((trip) => {
      const matchesSearch =
        trip.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        trip.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        trip.meetup_location?.toLowerCase().includes(searchTerm.toLowerCase())
      return matchesSearch
    })

    const groups: Record<TripGroup, TripResponse[]> = {
      active: [],
      planning: [],
      completed: []
    }
    filtered.forEach((trip) => {
      const group = getTripGroup(trip)
      groups[group].push(trip)
    })
    return groups
  }, [trips, searchTerm])

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden">
      <div className="p-4 border-b border-earth-200">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-earth-900 dark:text-foreground">Trips</h2>
          {headerRight != null && <div className="flex items-center gap-0.5">{headerRight}</div>}
        </div>
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-earth-400 dark:text-earth-500" />
          <Input
            placeholder="Search trips..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 h-9 bg-earth-100 border-earth-200"
          />
        </div>
        <Select value={sortOption} onValueChange={(v) => setSortOption(v as TripSortOption)}>
          <SelectTrigger className="h-9 bg-earth-100 border-earth-200 text-earth-800 dark:text-foreground text-xs">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            {TRIP_SORT_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value} className="text-xs">
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
        {error ? (
          <div className="p-4 text-sm text-error">{error}</div>
        ) : (
          <div className="p-2 space-y-6">
            {(['active', 'planning', 'completed'] as const).map((group) => {
              const items = groupedTrips[group]
              if (items.length === 0) return null
              const isExpanded = sectionExpanded[group]
              const isCollapsible = group !== 'active'
              const toggleSection = () => {
                setSectionExpanded((prev) => ({ ...prev, [group]: !prev[group] }))
              }
              const headerContent = (
                <>
                  {isCollapsible && (
                    <span className="flex-shrink-0 text-earth-500 dark:text-earth-400" aria-hidden>
                      {isExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                    </span>
                  )}
                  <span className="text-earth-800 dark:text-foreground">{getGroupLabel(group)}</span>
                  <span className="text-earth-600 dark:text-earth-400">({items.length})</span>
                </>
              )
              return (
                <div key={group}>
                  {isCollapsible ? (
                    <button
                      type="button"
                      onClick={toggleSection}
                      className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-xs font-medium text-earth-800 dark:text-foreground cursor-pointer hover:bg-earth-100 dark:hover:bg-earth-200 rounded-md transition-colors"
                      aria-expanded={isExpanded}
                      aria-label={`${getGroupLabel(group)} (${items.length}), ${isExpanded ? 'collapse' : 'expand'}`}
                    >
                      {headerContent}
                    </button>
                  ) : (
                    <div className="flex w-full items-center gap-2 px-2 py-1.5 text-xs font-medium text-earth-800 dark:text-foreground">
                      {headerContent}
                    </div>
                  )}
                  {isExpanded && (
                    <div className="mt-1 space-y-2">
                      {items.map((trip) => (
                        <TripSidebarItem
                          key={trip.id}
                          trip={trip}
                          coverUrl={getTripCoverUrl(trip.cover_photo_url ?? coverUrls[trip.id])}
                          isActive={location.pathname === `/trips/${trip.id}`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {!error && trips && trips.length === 0 && (
          <div className="flex flex-col items-center justify-center p-8 text-center">
            <p className="text-sm text-earth-500 dark:text-foreground mb-2">No trips yet</p>
            <p className="text-xs text-earth-400 dark:text-earth-300 mb-4">Create a trip to get started</p>
            <Button size="sm" onClick={() => openCreateModal()}>
              <Plus className="h-4 w-4 mr-2" />
              Create trip
            </Button>
          </div>
        )}
      </div>

      {tripId && (
        <div className="flex min-h-0 flex-shrink-0 flex-col max-h-[45vh] overflow-hidden">
          <SidebarRecentActivity tripId={tripId} />
        </div>
      )}
    </div>
  )
}

export interface TripsSidebarProps {
  collapsed?: boolean
  onToggleCollapse?: () => void
}

export function TripsSidebar({ collapsed = false, onToggleCollapse }: TripsSidebarProps) {
  const { trips, isLoading } = useTrips()
  const hasFetched = useTripHasFetched()
  const { openCreateModal } = useCreateModal()
  // Only show the skeleton before the very first fetch completes.
  // Using trips.length here causes TripsSidebarContent to unmount/remount on
  // every re-fetch for users with 0 active trips, creating an infinite loop.
  const isInitialListLoad = isLoading && !hasFetched

  // Collapsed rail: narrow strip with expand control and quick actions
  if (collapsed) {
    return (
      <aside
        className={cn(
          'hidden md:flex flex-col flex-shrink-0 h-full overflow-hidden border-r border-earth-200 bg-earth-50',
          'transition-[width] duration-200 ease-out'
        )}
        style={{ width: COLLAPSED_WIDTH }}
        aria-label="Trips sidebar collapsed"
      >
        <div className="flex flex-col items-center py-3 gap-2 border-b border-earth-200">
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9"
            onClick={onToggleCollapse}
            aria-label="Expand trips sidebar"
          >
            <PanelRightClose className="h-5 w-5 text-earth-600 dark:text-earth-300" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9"
            onClick={() => openCreateModal()}
            title="Create trip"
            aria-label="Create trip"
          >
            <Plus className="h-5 w-5 text-earth-600 dark:text-earth-300" />
          </Button>
        </div>
        <div className="flex-1" />
      </aside>
    )
  }

  if (isInitialListLoad) {
    return <TripsSidebarSkeleton />
  }

  return (
    <aside
      className={cn(
        'hidden md:flex flex-col flex-shrink-0 h-full overflow-hidden border-r border-earth-200 bg-earth-50',
        'w-full md:w-auto transition-[width] duration-200 ease-out'
      )}
      style={{ width: SIDEBAR_WIDTH }}
      aria-label="Trips sidebar"
    >
      <TripsSidebarContent
        headerRight={
          <>
            <Button
              size="sm"
              className="h-8 gap-1.5 px-2.5 text-xs shrink-0"
              onClick={() => openCreateModal()}
            >
              <Plus className="h-4 w-4" />
              Create trip
            </Button>
            {onToggleCollapse && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={onToggleCollapse}
                aria-label="Collapse sidebar"
              >
                <PanelRightClose className="h-5 w-5 text-earth-500 dark:text-earth-400" />
              </Button>
            )}
          </>
        }
      />
    </aside>
  )
}
